import { useState } from 'react';
import cn from 'classnames';
import {
  AuditResult,
  Rating,
  SECTION_LABELS,
  SkillTrace
} from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { SearchField } from '@Components/SearchField/SearchField';
import { groupBySection } from '../engine/health-score';
import { ResultsFilter, ResultsFilterType, SECTION_ORDER } from '../google-ads-audit-constants';

const RATING_LABELS: Record<Rating, string> = {
  ok: 'OK',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
  na: 'N/A'
};

interface RatingPillProps {
  rating: Rating;
}

const RatingPill = ({ rating }: RatingPillProps) => (
  <span className={cn('google-ads-audit-rating-pill', `rating-${rating}`)}>
    {RATING_LABELS[rating]}
  </span>
);

interface TraceViewProps {
  trace: SkillTrace;
}

const TraceView = ({ trace }: TraceViewProps) => {
  if (trace.kind === 'rule') {
    return (
      <div className='google-ads-audit-trace'>
        {trace.matchedRule && (
          <div className='google-ads-audit-trace-line'>Matched rule: {trace.matchedRule}</div>
        )}
        {trace.inputTotalRows !== undefined && (
          <div className='google-ads-audit-trace-line'>
            {trace.inputTotalRows} row{trace.inputTotalRows === 1 ? '' : 's'} evaluated
          </div>
        )}
      </div>
    );
  }
  return (
    <div className='google-ads-audit-trace'>
      {(trace.queries ?? []).map((query, i) => (
        <div key={i} className='google-ads-audit-trace-query'>
          <code>{query.gaql}</code>
          <span className='google-ads-audit-trace-meta'>
            {query.totalRows} row{query.totalRows === 1 ? '' : 's'}
            {query.truncated ? ' (truncated)' : ''}
            {query.error && ` — error: ${query.error}`}
          </span>
        </div>
      ))}
    </div>
  );
};

interface ResultRowProps {
  result: AuditResult;
}

// Expanding a row surfaces the evidence behind the finding — the actual data the check
// looked at — not a re-statement of the comment already visible in the row above it.
const ResultRow = ({ result }: ResultRowProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className='google-ads-audit-result-row'>
      <InteractiveDiv
        className='google-ads-audit-result-row-summary'
        onClick={() => setExpanded(v => !v)}
      >
        <span className='google-ads-audit-result-label'>{result.label}</span>
        <RatingPill rating={result.rating} />
        {result.comments && (
          <span className='google-ads-audit-result-comment'>{result.comments}</span>
        )}
      </InteractiveDiv>
      {expanded && (
        <div className='google-ads-audit-result-detail'>
          <span className='google-ads-audit-result-detail-label'>Evidence</span>
          {result.trace ? (
            <TraceView trace={result.trace} />
          ) : (
            <p className='google-ads-audit-result-no-evidence'>
              This run didn&apos;t capture the underlying data for this check — only the rating and
              finding above were kept.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface SectionGroupProps {
  sectionId: string;
  results: AuditResult[];
}

const SectionGroup = ({ sectionId, results }: SectionGroupProps) => (
  <div className='google-ads-audit-section-group'>
    <div className='google-ads-audit-section-group-title'>
      {SECTION_LABELS[sectionId as keyof typeof SECTION_LABELS] ?? sectionId}
    </div>
    <div className='google-ads-audit-result-header'>
      <span>Check</span>
      <span>Severity</span>
      <span>Finding</span>
    </div>
    {results.map(result => (
      <ResultRow key={result.skillKey} result={result} />
    ))}
  </div>
);

interface AuditResultsProps {
  results: AuditResult[];
  activeSection?: string;
}

export const AuditResults = ({ results, activeSection = 'all' }: AuditResultsProps) => {
  const [filter, setFilter] = useState<ResultsFilterType>(ResultsFilter.ALL);
  const [search, setSearch] = useState('');

  const filtered =
    filter === ResultsFilter.ALL ? results : results.filter(r => r.rating === filter);
  const scoped =
    activeSection === 'all' ? filtered : filtered.filter(r => r.sectionId === activeSection);

  const query = search.trim().toLowerCase();
  const searched = query
    ? scoped.filter(
        r =>
          r.label.toLowerCase().includes(query) || (r.comments ?? '').toLowerCase().includes(query)
      )
    : scoped;

  const bySection = groupBySection(searched);
  const orderedSections = [
    ...SECTION_ORDER.filter(s => bySection[s]),
    ...Object.keys(bySection).filter(s => !(SECTION_ORDER as string[]).includes(s))
  ];

  if (results.length === 0) {
    return <div className='google-ads-audit-empty-state'>No results yet.</div>;
  }

  return (
    <div className='google-ads-audit-results'>
      <div className='google-ads-audit-results-filters'>
        <SearchField
          className='google-ads-audit-search'
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Search findings…'
          aria-label='Search findings'
        />
        {Object.values(ResultsFilter).map(value => (
          <InteractiveDiv
            key={value}
            className={cn('google-ads-audit-filter-chip', { active: filter === value })}
            onClick={() => setFilter(value)}
          >
            {RATING_LABELS[value as Rating] ?? 'All'}
          </InteractiveDiv>
        ))}
      </div>
      {orderedSections.length === 0 ? (
        <div className='google-ads-audit-empty-state'>
          No findings match {query ? `“${search.trim()}”` : 'the current filters'}.
        </div>
      ) : (
        orderedSections.map(sectionId => (
          <SectionGroup key={sectionId} sectionId={sectionId} results={bySection[sectionId]} />
        ))
      )}
    </div>
  );
};
